import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { Button } from '@spark/ui-components/components/Button';
import { COLUNAS_SEMPRE_VISIVEIS, LAYOUT_PADRAO, ordenarIds } from '../utils/layoutColunas';

/**
 * Ordem (arrastar ou setas) e visibilidade das colunas. Vale para os dois turnos e fica salvo neste navegador.
 */
const ProgramacaoHavanColunasModal = ({ show, colunas, layout, onClose, onSalvar }) => {
  const [ordem, setOrdem] = useState([]);
  const [ocultas, setOcultas] = useState([]);
  // Larguras vêm do arraste da borda do cabeçalho; aqui só "Restaurar padrão" mexe nelas (volta ao tamanho padrão).
  const [larguras, setLarguras] = useState({});
  const [arrastando, setArrastando] = useState(null);
  const idsPadrao = colunas.map(coluna => coluna.id);
  const rotulo = Object.fromEntries(colunas.map(coluna => [coluna.id, coluna.header]));

  useEffect(() => {
    if (!show) return;
    setOrdem(ordenarIds(colunas.map(coluna => coluna.id), layout?.ordem));
    setOcultas((layout?.ocultas || []).filter(id => !COLUNAS_SEMPRE_VISIVEIS.includes(id)));
    setLarguras(layout?.larguras || {});
    setArrastando(null);
  }, [show, colunas, layout]);

  const mover = (id, destino) => setOrdem(atual => {
    const origem = atual.indexOf(id);
    if (origem < 0 || destino < 0 || destino >= atual.length || origem === destino) return atual;
    const proxima = [...atual];
    proxima.splice(origem, 1);
    proxima.splice(destino, 0, id);
    return proxima;
  });

  const alternar = id => setOcultas(atual => (atual.includes(id) ? atual.filter(oculta => oculta !== id) : [...atual, id]));

  const salvar = () => {
    // Igual ao padrão: não guarda nada (a tela volta a seguir a ordem padrão, inclusive com colunas novas).
    const ordemPadrao = ordem.length === idsPadrao.length && ordem.every((id, indice) => id === idsPadrao[indice]);
    onSalvar?.({ ordem: ordemPadrao ? [] : ordem, ocultas, larguras });
  };

  return (
    <Modal show={show} onHide={onClose} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Colunas</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Dica>
          Arraste pelo ⠿ ou use as setas para mudar a ordem. Para mudar a largura, arraste a borda direita do título da coluna na tabela.
          Vale para os dois turnos e fica salvo neste navegador.
          {Object.keys(larguras).length ? ` ${Object.keys(larguras).length} coluna(s) com largura ajustada.` : ''}
        </Dica>
        <Lista>
          {ordem.map((id, indice) => {
            const fixa = COLUNAS_SEMPRE_VISIVEIS.includes(id);
            return (
              <Item
                key={id}
                draggable
                $arrastando={arrastando === id}
                onDragStart={event => { setArrastando(id); event.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={event => { event.preventDefault(); if (arrastando && arrastando !== id) mover(arrastando, indice); }}
                onDragEnd={() => setArrastando(null)}
                onDrop={event => { event.preventDefault(); setArrastando(null); }}
              >
                <Alca aria-hidden="true">⠿</Alca>
                <label htmlFor={`coluna-havan-${id}`}>
                  <input
                    id={`coluna-havan-${id}`}
                    type="checkbox"
                    checked={!ocultas.includes(id)}
                    disabled={fixa}
                    onChange={() => alternar(id)}
                  />
                  <span>{rotulo[id] || id}</span>
                  {fixa ? <Fixa>sempre visível</Fixa> : null}
                </label>
                <Setas>
                  <button type="button" aria-label={`Subir ${rotulo[id] || id}`} disabled={indice === 0} onClick={() => mover(id, indice - 1)}>↑</button>
                  <button type="button" aria-label={`Descer ${rotulo[id] || id}`} disabled={indice === ordem.length - 1} onClick={() => mover(id, indice + 1)}>↓</button>
                </Setas>
              </Item>
            );
          })}
        </Lista>
      </Modal.Body>
      <Modal.Footer>
        <Restaurar type="button" onClick={() => { setOrdem(idsPadrao); setOcultas(LAYOUT_PADRAO.ocultas); setLarguras(LAYOUT_PADRAO.larguras); }}>Restaurar padrão</Restaurar>
        <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button type="button" variant="primary" onClick={salvar}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

ProgramacaoHavanColunasModal.propTypes = {
  show: PropTypes.bool,
  colunas: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, header: PropTypes.string })),
  layout: PropTypes.shape({ ordem: PropTypes.arrayOf(PropTypes.string), ocultas: PropTypes.arrayOf(PropTypes.string), larguras: PropTypes.objectOf(PropTypes.number) }),
  onClose: PropTypes.func,
  onSalvar: PropTypes.func
};

ProgramacaoHavanColunasModal.defaultProps = {
  show: false,
  colunas: [],
  layout: LAYOUT_PADRAO,
  onClose: undefined,
  onSalvar: undefined
};

export default ProgramacaoHavanColunasModal;

const Dica = styled.p`margin: 0 0 10px; color: #526275; font-size: 12px;`;
const Lista = styled.ul`display: flex; flex-direction: column; gap: 4px; margin: 0; padding: 0; list-style: none;`;
const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid ${({ $arrastando }) => ($arrastando ? '#163455' : '#e2e8f0')};
  border-radius: 4px;
  background: ${({ $arrastando }) => ($arrastando ? '#eef3f8' : '#ffffff')};
  font-size: 13px;
  label { display: flex; flex: 1; align-items: center; gap: 8px; margin: 0; cursor: pointer; }
  input { width: 15px; height: 15px; margin: 0; accent-color: #163455; }
`;
const Alca = styled.span`color: #94a3b8; font-size: 16px; cursor: grab;`;
const Fixa = styled.span`color: #8a96a3; font-size: 11px;`;
const Setas = styled.span`
  display: inline-flex;
  gap: 2px;
  button {
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #ffffff;
    color: #163455;
    font-weight: 700;
    line-height: 1;
  }
  button:disabled { opacity: 0.35; }
`;
const Restaurar = styled.button`
  margin-right: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #163455;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
`;
